import {
  all,
  call,
  fork,
  put,
  race,
  select,
  take,
  takeEvery
} from "redux-saga/effects";
import { delay } from "redux-saga";
import onRouteEnter from "modules/routing/on-route-enter-saga";
import {
  destroy,
  getFormValues,
  initialize,
  isDirty,
  isPristine,
  reset
} from "redux-form";
import { actions as routerActions } from "redux-router5";

import _ from "lodash";

import Actions from "modules/diagnostic/diagnostic-actions";
import { getQuestionFormName } from "modules/diagnostic/diagnostic-utils";
import * as Schema from "modules/entity-repository/schema";
import * as DiagnosticSelectors from "modules/diagnostic/diagnostic-selectors";
import {
  INDEX,
  SELF_DIAGNOSIS_FINISHED,
  SELF_DIAGNOSIS_GENERAL_INFO
} from "modules/routing/routes";
import {
  DiagnosticViews,
  QuestionKeys,
  QuestionSubTypes,
  QuestionTypes
} from "modules/diagnostic/diagnostic-constants";
import { normalizeAndStore } from "modules/entity-repository/entity-repository-saga";
import { getUserId } from "modules/user/user-selectors";
import * as EntityRepoSelectors from "modules/entity-repository/entity-repository-selectors";
import { validateQuestion } from "modules/utils/validations";
import { runModal } from "modules/modal/modal-saga";
import { ModalType } from "modules/modal/modal-constants";
import * as API from "modules/diagnostic/diagnostic-api-saga";
import { updateUser } from "../../modules/api/user-effects";
import {
  composeSaga,
  withErrorHandlingNoRethrow
} from "modules/root/saga-helpers";
import { ANIMATION_DURATION } from "modules/general-info/question-form-constants";
import ModalActions from "modules/modal/modal-actions";
import { getExistingFormIds } from "modules/forms/forms-selectors";
import { FORM_NAME as sexAndAgeFormName } from "modules/general-info/sex-and-age-form";
import CommonActions from "modules/common/common-actions";
import {
  convertBirthdayToAge,
  formatBirthdayForBackend
} from "./diagnostic-utils";

function* getInitialDiagnostic() {
  const userId = yield select(getUserId);
  const sexFormValues = yield select(getFormValues(sexAndAgeFormName));
  const type = QuestionTypes.SINGLE;
  const answers = sexFormValues && {
    //birthday is not saved to the DB but age
    //shit implementation
    [QuestionKeys.AGE]: {
      type,
      value: convertBirthdayToAge(sexFormValues.birthday)
    },
    [QuestionKeys.SEX]: { type, value: sexFormValues.sex === "male" ? 1 : 0 },
    [QuestionKeys.WEIGHT]: { type, value: sexFormValues.weight },
    [QuestionKeys.HEIGHT]: { type, value: sexFormValues.height }
  };
  const resp = yield call(API.createDiagnostic, userId, answers);
  const userDetails = {
    birthday: formatBirthdayForBackend(sexFormValues.birthday),
    medical_properties: {
      age: convertBirthdayToAge(sexFormValues.birthday),
      sex: sexFormValues.sex === "male" ? 1 : 0,
      weight: parseFloat(sexFormValues.weight),
      height: parseFloat(sexFormValues.height)
    }
  };

  //update user profile
  yield call(updateUser, userDetails);

  const diagnostic = resp.data.data;
  const id = yield call(normalizeAndStore, diagnostic, Schema.diagnostic);
  yield put(Actions.Creators.initialDiagnosticFetched(id));
  // setting the first question as active
  const questionKey = Object.keys(diagnostic.result.questions)[0];
  if (questionKey) {
    yield put(
      Actions.Creators.setAnimationState(
        "none",
        "forward",
        "enter",
        diagnostic.result.questions[questionKey].id
      )
    );
    // yield put(
    //   Actions.Creators.currentQuestionChanged(
    //     diagnostic.result.questions[questionKey].id
    //   )
    // );
  }
  yield call(onGeneralInfoRoute);
  return id;
}

function* getQuestions(questionIds) {
  if (!questionIds || !questionIds.length) {
    return;
  }

  const resps = yield all(questionIds.map(id => call(API.fetchQuestion, id)));

  const isHumanBodyScreen = _.find(resps, function(res) {
    return (
      res.data &&
      res.data.data &&
      res.data.data.key &&
      res.data.data.key === QuestionKeys.HUMAN_MODEL
    );
  });

  if (isHumanBodyScreen) {
    yield call(runModal, ModalType.HUMAN_INFO);
  }

  yield all(
    resps.map(resp => call(normalizeAndStore, resp.data.data, Schema.question))
  );
}

function* getDiagnosticDetails(diagnostic) {
  yield call(getQuestions, diagnostic.result.questions);
  const diagnosticSessionResp = yield call(
    API.fetchDiagnosticSessionAnswers,
    diagnostic.id
  );
  const diagnosticSession = diagnosticSessionResp.data.data;
  return yield call(
    normalizeAndStore,
    diagnosticSession,
    Schema.diagnosticSession
  );
}

function* getQuestionsToSubmit(step) {
  const diagnostic = yield select(DiagnosticSelectors.getCurrentDiagnostic);
  const diagnosticSession = yield select(
    DiagnosticSelectors.getCurrentDiagnosticSession
  );

  // Questions from current step of diagnostic (not editing previous answers)
  if (step === diagnostic.step) {
    const questions = yield select(EntityRepoSelectors.getQuestions);
    return diagnostic.result.questions.map(id => questions[id]);
  } else {
    const diagnosticSessionQuestions = diagnosticSession.result.questions;
    const questionKeys = Object.keys(diagnosticSessionQuestions);
    return questionKeys
      .map(key => diagnosticSessionQuestions[key])
      .filter(item => item.step === step);
  }
}

function getRequestValue(answer, formValue) {
  const toInt = val => parseInt(val, 10);
  if (answer.type === QuestionTypes.MULTIPLE) {
    return typeof value === "string"
      ? [toInt(formValue)]
      : formValue.map(toInt).filter(item => typeof item === "number");
  } else if (answer.subtype === QuestionSubTypes.FREE_TEXT) {
    return formValue;
  } else {
    return toInt(formValue);
  }
}

function getAnswerType(answer) {
  if (answer.subtype === QuestionSubTypes.FREE_TEXT) {
    return QuestionSubTypes.FREE_TEXT;
  }
  return answer.type;
}

function getAnswerObject(questions, formValues) {
  const questionAnswerByKey = questions.reduce(
    (acc, item) => ({
      ...acc,
      [item.key]: item.answer
    }),
    {}
  );
  return Object.keys(formValues)
    .filter(key => Boolean(formValues[key]))
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          type: getAnswerType(questionAnswerByKey[key]),
          value: getRequestValue(
            questionAnswerByKey[key],
            formValues[key].answer
          )
        }
      }),
      {}
    );
}

function* getQuestionsAnswers(questions) {
  const formValues = yield all(
    questions.reduce((acc, question) => {
      return {
        ...acc,
        [question.key]: select(getFormValues(getQuestionFormName(question.id)))
      };
    }, {})
  );
  return getAnswerObject(questions, formValues);
}

function* fetchCurrentDiagnostic() {
  const diagId = yield select(DiagnosticSelectors.getCurrentDiagnosticId);
  const resp = yield call(API.fetchDiagnostic, diagId);
  return resp.data.data;
}

/**
 * Once the diagnostic is finished, it gets blocked
 * in order to change previous answers we need to unblock it first
 */
function* unblockDiagnosticIfRequired() {
  const diagnostic = yield select(DiagnosticSelectors.getCurrentDiagnostic);

  if (diagnostic.finished_at) {
    yield call(API.unblockDiagnostic, diagnostic.id);
  }
}

// ON ROUTES
function* onGeneralInfoRoute() {
  const diagnostic = yield call(fetchCurrentDiagnostic);

  yield call(getDiagnosticDetails, diagnostic);
  const id = yield call(normalizeAndStore, diagnostic, Schema.diagnostic);
  yield put(Actions.Creators.initialDiagnosticFetched(id));
  const resps = yield all(
    DiagnosticViews.map(view => call(API.fetchPainAreas, view))
  );
  const painZoneIds = yield all(
    resps.map(resp =>
      call(normalizeAndStore, resp.data.data, [Schema.painZone])
    )
  );
  yield put(Actions.Creators.painZonesFetched(...painZoneIds));
  yield put(Actions.Creators.setAnimationState("active"));
  yield call(delay, ANIMATION_DURATION);
  yield put(Actions.Creators.setAnimationState("done"));
}

function* closeThinkingModal(premise) {
  if (premise) {
    // Thinking modal is manually controlled, therefore we have to close it programatically
    yield put(ModalActions.Creators.closeModal());
  }
}

/**
 * Sends the answers to backend and fetches the updated diagnostic with questions
 * @param {number} step - step to submit the answers for
 * @param {number} diagnosticId - related diagnostic id
 */
function* submitStep(step, diagnosticId) {
  yield call(unblockDiagnosticIfRequired);
  const questions = yield call(getQuestionsToSubmit, step);
  const answers = yield call(getQuestionsAnswers, questions);
  const hasHumanBodyQuestion = questions.some(
    question => question.key === QuestionKeys.HUMAN_MODEL
  );
  const submitStepProcessName = "submitStep";

  try {
    if (hasHumanBodyQuestion) {
      yield put(
        CommonActions.Creators.startUiBlockingProcess(submitStepProcessName)
      );
    }
    yield call(API.submitDiagnosticSessionAnswer, answers, step, diagnosticId);
    // loading updated diagnostic
    const diagnostic = yield call(fetchCurrentDiagnostic);
    // diagnostic is finished
    if (diagnostic.result_status) {
      const treatment =
        diagnostic.result_treatments && diagnostic.result_treatments[0];
      return yield put(
        routerActions.navigateTo(SELF_DIAGNOSIS_FINISHED, {
          treatment
        })
      );
    }
    if (!diagnostic) {
      return;
    }
    yield call(getDiagnosticDetails, diagnostic);
    const id = yield call(normalizeAndStore, diagnostic, Schema.diagnostic);
    yield put(Actions.Creators.initialDiagnosticFetched(id));
  } finally {
    if (hasHumanBodyQuestion) {
      yield put(
        CommonActions.Creators.endUiBlockingProcess(submitStepProcessName)
      );
    }
  }
}

/**
 * Since we are handling form submission ourselves,
 * we need to clear dirty flags
 * @param {string} id - form name
 */
function* reinitializeForm(id) {
  const values = yield select(getFormValues(id));
  // 3rd arg === keepDirty
  yield put(initialize(id, values, false));
}

/**
 * Clears the forms
 * @param {array} formIds - array of the forms
 * @param {array} dirtyFlags - array of the dirty flags reflecting the order of formIds
 */
function* reinitializeForms(formIds, dirtyFlags) {
  const toReinitialize = formIds.filter((_, idx) => dirtyFlags[idx]);
  yield all(toReinitialize.map(id => call(reinitializeForm, id)));
}

const hasSomeAnswerChanged = questionFormsDirtyFlags =>
  questionFormsDirtyFlags.some(item => item === true);

function* validateQuestions(formIds) {
  const answers = yield all(
    formIds.map(formId => select(getFormValues(formId)))
  );
  const isValidFlags = answers.map(values => {
    const result = validateQuestion(values);
    return !Boolean(Object.keys(result).length);
  });
  return !isValidFlags.some(item => item === false);
}

function* movingNextQuestion({ questionId } = {}) {
  yield put(Actions.Creators.setMovingNext(true));
  yield call(animateQuestionOut, "forward");
  const adjacentQuestions = yield select(
    DiagnosticSelectors.getAdjacentQuestions
  );
  const continueNavigation = yield call(
    onQuestionNavigate,
    adjacentQuestions.current,
    adjacentQuestions.next
  );
  let regularNextQuestionId;
  if (continueNavigation) {
    const newAdjacentQuestions = yield select(
      DiagnosticSelectors.getAdjacentQuestions
    );
    if (newAdjacentQuestions.next) {
      regularNextQuestionId = newAdjacentQuestions.next.id;
    }
  }
  yield call(animateQuestionIn, "forward", questionId || regularNextQuestionId);
  yield put(Actions.Creators.setMovingNext(false));
}

function* onMoveNextQuestion({ questionId } = {}) {
  const movingNext = yield select(DiagnosticSelectors.getMovingNext);
  if (movingNext) {
    return;
  }
  yield call(movingNextQuestion, { questionId });
}

function* onMovePreviousQuestion({ questionId } = {}) {
  yield call(animateQuestionOut, "backward");
  const adjacentQuestions = yield select(
    DiagnosticSelectors.getAdjacentQuestions
  );
  const continueNavigation = yield call(
    onQuestionNavigate,
    adjacentQuestions.current,
    adjacentQuestions.previous
  );
  // Even if the navigation actions failed (e.g. because all required answers were not filled in)
  // we still want to allow to navigate backwards to previous question
  let regularNextQuestionId;
  if (continueNavigation || adjacentQuestions.previous) {
    const newAdjacentQuestions = yield select(
      DiagnosticSelectors.getAdjacentQuestions
    );
    if (newAdjacentQuestions.previous) {
      regularNextQuestionId = newAdjacentQuestions.previous.id;
    }
  }
  yield call(
    animateQuestionIn,
    "backward",
    questionId || regularNextQuestionId
  );
}

const areQuestionsFromTheSameStep = (currentQuestion, followingQuestion) =>
  // followingQuestion is null if we are on the last question of the particular step and we have never been to the next step
  followingQuestion && followingQuestion.step === currentQuestion.step;

const shouldSubmitStep = (questionFormsDirtyFlags, followingQuestion) =>
  // If following question is unknown we need to submit the step to get it
  !followingQuestion || hasSomeAnswerChanged(questionFormsDirtyFlags);

function* getQuestionFormIds(step) {
  const allQuestions = yield select(
    DiagnosticSelectors.getAllCurrentDiagnosticQuestions
  );
  return allQuestions
    .filter(item => item.step === step)
    .map(item => getQuestionFormName(item.id));
}

function* getNextQuestionFormIds(step) {
  const allQuestions = yield select(EntityRepoSelectors.getQuestions);
  return Object.values(allQuestions)
    .filter(item => item.step > step)
    .map(item => getQuestionFormName(item.id));
}

/**
 * Tries to submit an answers for a specific step.
 * Handles the validation, in order to succeed all fields must be valid
 * otherwise false is returned to reflect such event.
 * If submission is successfull it reinitializes all dirty forms.
 * @param {number} step - the current diagnostic step to be commited
 * @param {array<string>} currentStepQuestionFormIds - array of form IDs of all questions from the given step
 * @param {array<string>} questionFormsDirtyFlags - array of boolean flags notating if the question form was changed. The order must be the same as currentStepQuestionFormIds
 * @returns {boolean} result of the validation of the forms
 */
function* trySubmitStep(
  step,
  currentStepQuestionFormIds,
  questionFormsDirtyFlags
) {
  const canSubmitStep = yield call(
    validateQuestions,
    currentStepQuestionFormIds
  );
  if (!canSubmitStep) {
    return false;
  }

  const diagnosticId = yield select(DiagnosticSelectors.getCurrentDiagnosticId);

  yield call(submitStep, step, diagnosticId);
  // TODO Don't clear the form if submit failed
  // Since we are not using form - submit to send the data to the server, we need to manually clear the form
  yield call(
    reinitializeForms,
    currentStepQuestionFormIds,
    questionFormsDirtyFlags
  );
  return true;
}
/**
 * Tries to perform actions which are necessary for traversing between currentQuestion and the followingQuestion.
 * Returns boolean flag stating if all of the necessary actions were succesfully done.
 * @param {object} currentQuestion - currently displayed question,
 * @param {object} followingQuestion - question which should follow (if we already know it) based on the navigation direction (Previous/Next),
 * @returns {boolean} whether all actions required when navigating to the followingQuestions were succesfully done and we can continue with the navigation process.
 */
function* onQuestionNavigate(currentQuestion, followingQuestion) {
  if (areQuestionsFromTheSameStep(currentQuestion, followingQuestion)) {
    // no actions necessary, just switching between the questions of the same step (not requiring to actually answer the question)
    return true;
  }

  const currentStepQuestionFormIds = yield call(
    getQuestionFormIds,
    currentQuestion.step
  );
  const questionFormsDirtyFlags = yield all(
    currentStepQuestionFormIds.map(id => select(isDirty(id)))
  );
  if (!shouldSubmitStep(questionFormsDirtyFlags, followingQuestion)) {
    // no actions neccesary
    return true;
  }

  // Step needs to be submitted in order to navigate to the followingQuestion
  const stepSubmitSucceeded = yield call(
    trySubmitStep,
    currentQuestion.step,
    currentStepQuestionFormIds,
    questionFormsDirtyFlags
  );

  return stepSubmitSucceeded;
}

function* onDisplayHelp() {
  const currentQuestion = yield select(DiagnosticSelectors.getCurrentQuestion);
  if (currentQuestion.key === QuestionKeys.HUMAN_MODEL) {
    yield call(runModal, ModalType.PAIN_PROFILE_SCREEN);
  } else {
    yield call(runModal, ModalType.SELF_DIAGNOSIS_SCREEN);
  }
}

function* onJumpQuestion({ questionId }) {
  const allQuestions = yield select(
    DiagnosticSelectors.getAllCurrentDiagnosticQuestions
  );
  const currentQuestionId = yield select(
    DiagnosticSelectors.getCurrentQuestionId
  );
  const questionIds = allQuestions.map(item => item.id);
  if (currentQuestionId === questionId) {
    return;
  }
  const questionIndex = questionIds.indexOf(questionId);
  const currentQuestionIndex = questionIds.indexOf(currentQuestionId);
  if (questionIndex > currentQuestionIndex) {
    yield call(movingNextQuestion, { questionId });
  } else {
    yield call(onMovePreviousQuestion, { questionId });
  }
}

const TOTAL_TRANSITION_DURATION = 1000;
function* glowAnswerFieldAndContinue() {
  const questionAtStart = yield select(DiagnosticSelectors.getCurrentQuestion);
  if (
    questionAtStart &&
    questionAtStart.answer &&
    (questionAtStart.answer.type !== QuestionTypes.SINGLE ||
      questionAtStart.answer.subtype === QuestionSubTypes.RANGE ||
      questionAtStart.answer.subtype === QuestionSubTypes.FREE_TEXT)
  ) {
    // Auto-transition only SINGLE answer questions
    return;
  }
  // Setting glow of the answer to indicate user something is in action
  yield put(Actions.Creators.setAnswerGlow(true));
  yield put(Actions.Creators.setMovingNext(true));
  // waiting whether user won't change question in time
  let { timeout } = yield race({
    timeout: call(delay, TOTAL_TRANSITION_DURATION),
    reset: take(Actions.Types.ON_CHANGE_ANSWER)
  });
  // if user didn't changed question we continue with navigate
  if (timeout) {
    // if user waited long enough we change the question
    const currentQuestionId = yield select(
      DiagnosticSelectors.getCurrentQuestionId
    );
    if (questionAtStart.id === currentQuestionId) {
      yield call(movingNextQuestion);
    }
  }
  yield put(Actions.Creators.setMovingNext(false));
}

function* destroyNextForms(currentQuestionStep) {
  const followingStepsQuestionFormIds = yield call(
    getNextQuestionFormIds,
    currentQuestionStep
  );
  const existingFormIds = yield select(getExistingFormIds);
  yield all(
    followingStepsQuestionFormIds
      .filter(formName => existingFormIds.includes(formName))
      .map(formName => put(destroy(formName)))
  );
}

/**
 * Pops up the warning on requirement to answer all consequential answers
 * when changing previous one. There are 3 cases which won't trigger the modal:
 *  1/ Changing answer on last(active) step/question
 *  2/ Already confirmed the modal previously (we want to dispaly it only once)
 *  3/ If the form is pristine - we are using `reset` to rollback the values
 *     in case user requests so, but the reset triggers onChange too therefore
 *     we check for the pristinity.
 */
function* onChangeAnswer() {
  const alreadyConfirmedChange = yield select(
    DiagnosticSelectors.getChangeAnswerConfirmation
  );
  const currentQuestion = yield select(DiagnosticSelectors.getCurrentQuestion);
  const currentQuestionStep = currentQuestion.step;
  const currentDiagnostic = yield select(
    DiagnosticSelectors.getCurrentDiagnostic
  );
  const currentFormName = getQuestionFormName(currentQuestion.id);
  const currentFormIsPristine = yield select(isPristine(currentFormName));
  if (
    alreadyConfirmedChange ||
    currentDiagnostic.step === currentQuestionStep ||
    currentFormIsPristine
  ) {
    // If single option question -> we want to auto-continue in the flow after some timeout
    yield call(destroyNextForms, currentQuestionStep);
    yield call(glowAnswerFieldAndContinue);
    return;
  }
  const result = yield call(runModal, ModalType.QUESTION_CHANGED_WARNING);
  if (result) {
    yield put(Actions.Creators.setChangeAnswerConfirmed(true));
    yield call(destroyNextForms, currentQuestionStep);
  } else {
    const currentStepQuestionFormIds = yield call(
      getQuestionFormIds,
      currentQuestionStep
    );
    yield all(currentStepQuestionFormIds.map(formName => put(reset(formName))));
  }
  // If single option question -> we want to auto-continue in the flow after some timeout
  yield call(glowAnswerFieldAndContinue);
}

function* takeFirst(pattern, saga, ...args) {
  const task = yield fork(function*() {
    while (true) {
      const action = yield take(pattern);
      yield call(saga, ...args.concat(action));
    }
  });
  return task;
}

function* onDiagnosticExit() {
  const result = yield call(runModal, ModalType.EXIT_DIAGNOSTIC);
  if (result) {
    yield put(routerActions.navigateTo(INDEX));
  }
}
function* onConsentMedical() {
  yield call(runModal, ModalType.CONSENT_MEDICAL);
}
function* onConsentTerms() {
  yield call(runModal, ModalType.CONSENT_TERMS);
}
function* onConsentPrivacy() {
  yield call(runModal, ModalType.CONSENT_PRIVACY);
}

function* animateQuestionOut(direction) {
  yield put(Actions.Creators.setAnimationState("none", direction, "exit"));
  yield call(delay, 1);
  yield put(Actions.Creators.setAnimationState("active"));
  yield call(delay, ANIMATION_DURATION);
  yield put(Actions.Creators.setAnimationState("done"));
}

function* animateQuestionIn(direction, questionId) {
  yield put(
    Actions.Creators.setAnimationState("none", direction, "enter", questionId)
  );
  yield call(delay, 1);
  yield put(Actions.Creators.setAnimationState("active"));
  yield call(delay, ANIMATION_DURATION);
  yield put(Actions.Creators.setAnimationState("done"));
}

export default function* diagnosticSaga() {
  yield all([
    onRouteEnter(
      SELF_DIAGNOSIS_GENERAL_INFO,
      composeSaga(withErrorHandlingNoRethrow, getInitialDiagnostic)
    ),
    takeEvery(
      Actions.Types.ON_DIAGNOSTIC_EXIT,
      composeSaga(withErrorHandlingNoRethrow, onDiagnosticExit)
    ),
    takeEvery(
      Actions.Types.MOVE_TO_NEXT_QUESTION,
      composeSaga(withErrorHandlingNoRethrow, onMoveNextQuestion)
    ),
    takeEvery(
      Actions.Types.MOVE_TO_PREVIOUS_QUESTION,
      composeSaga(withErrorHandlingNoRethrow, onMovePreviousQuestion)
    ),
    takeEvery(
      Actions.Types.DISPLAY_HELP,
      composeSaga(withErrorHandlingNoRethrow, onDisplayHelp)
    ),
    takeFirst(
      Actions.Types.ON_CHANGE_ANSWER,
      composeSaga(withErrorHandlingNoRethrow, onChangeAnswer)
    ),
    takeEvery(
      Actions.Types.JUMP_TO_QUESTION,
      composeSaga(withErrorHandlingNoRethrow, onJumpQuestion)
    ),
    takeEvery(
      Actions.Types.CONSENT_MEDICAL,
      composeSaga(withErrorHandlingNoRethrow, onConsentMedical)
    ),
    takeEvery(
      Actions.Types.CONSENT_TERMS,
      composeSaga(withErrorHandlingNoRethrow, onConsentTerms)
    ),
    takeEvery(
      Actions.Types.CONSENT_PRIVACY,
      composeSaga(withErrorHandlingNoRethrow, onConsentPrivacy)
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/modules/diagnostic/diagnostic-saga.js